import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Paper,
    Button,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    CalendarToday as CalendarIcon,
    Schedule as ScheduleIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useNavigate } from 'react-router-dom';
import WeekHeader from './WeekHeader';
import WeekGrid from './WeekGrid';
import PostDetailsSlideover from '../PostDetailsSlideover';

// Extend dayjs with ISO week plugin
dayjs.extend(isoWeek);

const HomeWeekCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden',
    boxSizing: 'border-box',
    minHeight: '360px',
    maxHeight: '420px',
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(2),
        borderRadius: theme.spacing(1),
        minHeight: '320px',
    },
}));

const EmptyWeekMessage = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '280px',
}));

const CardHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.5),
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        '& > *:last-child': {
            alignSelf: 'stretch',
        },
    },
}));

/**
 * HomeWeekView Component
 * 
 * A simplified week view for the home tab that shows only the current week.
 * Includes a button to navigate to the full calendar view for more functionality.
 * 
 * @param {Object} props
 * @param {Array} props.posts - Array of post objects for the current week
 * @param {Function} props.onPostClick - Callback when a post is clicked
 * @param {boolean} props.loading - Whether posts are currently being fetched
 * @param {Function} props.onNavigateToCalendar - Callback to navigate to calendar tab
 */
const HomeWeekView = ({ posts = [], onPostClick, loading = false, onNavigateToCalendar }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    
    // State management
    const [selectedPost, setSelectedPost] = useState(null);
    const [slideoverOpen, setSlideoverOpen] = useState(false);
    
    // Always show current week
    const currentWeek = dayjs();

    // Handle post click
    const handlePostClick = (post) => {
        setSelectedPost(post);
        setSlideoverOpen(true);
        if (onPostClick) {
            onPostClick(post);
        }
    };

    // Handle slideover close
    const handleSlideoverClose = () => {
        setSlideoverOpen(false);
        setSelectedPost(null);
    };

    // Handle navigate to calendar
    const handleViewFullCalendar = () => {
        if (onNavigateToCalendar) {
            onNavigateToCalendar();
        } else {
            // Default navigation logic if no callback provided
            navigate('/dashboard?tab=1'); // Assuming calendar is tab 1
        }
    };

    // Filter posts for current week
    const currentWeekPosts = posts.filter(post => {
        const postDate = dayjs(post.created_at || post.scheduled_post_date);
        return postDate.isSame(currentWeek, 'isoWeek');
    });

    return (
        <HomeWeekCard elevation={2}>
            <CardHeader>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ScheduleIcon color="primary" />
                    <Typography variant="h6" component="h2">
                        This Week's Schedule
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CalendarIcon />}
                    onClick={handleViewFullCalendar}
                    sx={{ minWidth: 'auto' }}
                >
                    {isMobile ? 'Calendar' : 'View Full Calendar'}
                </Button>
            </CardHeader>

            {/* Week Header - Display only, no navigation */}
            <WeekHeader
                currentWeek={currentWeek}
                showNavigation={false}
                showTodayButton={false}
            />

            {/* Week Content */}
            {!loading && currentWeekPosts.length === 0 ? (
                <EmptyWeekMessage>
                    <ScheduleIcon sx={{ fontSize: 48, mb: 1.5, opacity: 0.5 }} />
                    <Typography 
                        variant="body1" 
                        fontWeight="600"
                        gutterBottom
                        sx={{ 
                            fontSize: '1rem',
                            lineHeight: '1.5rem',
                            mb: 1
                        }}
                    >
                        No posts scheduled for this week
                    </Typography>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1.5,
                        flexWrap: 'wrap',
                        justifyContent: 'center'
                    }}>
                        <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                                fontSize: '0.8125rem',
                                lineHeight: '1.25rem'
                            }}
                        >
                            Create your first post to see it appear in your schedule
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={handleViewFullCalendar}
                            startIcon={<CalendarIcon />}
                            size="small"
                            sx={{ minWidth: 'auto' }}
                        >
                            Open Calendar
                        </Button>
                    </Box>
                </EmptyWeekMessage>
            ) : (
                <WeekGrid
                    posts={currentWeekPosts}
                    currentWeek={currentWeek}
                    onPostClick={handlePostClick}
                    loading={loading}
                    height="280px"
                    showEmptyDays={false}
                />
            )}

            {/* Post Details Slideover */}
            <PostDetailsSlideover
                open={slideoverOpen}
                onClose={handleSlideoverClose}
                post={selectedPost}
            />
        </HomeWeekCard>
    );
};

export default HomeWeekView; 